import type { AisleDefinition, AisleWalkthrough, CampaignPlacement, StoreImage, StoreProfileDetail } from '../types';

export interface AisleMediaSummary {
  aisle: AisleDefinition;
  walkthroughs: AisleWalkthrough[];
  installImages: StoreImage[];
  campaignCount: number;
  hasWalkthrough: boolean;
  hasInstallImages: boolean;
}

export function dedupeAislesByNumber(aisles: AisleDefinition[]): AisleDefinition[] {
  const byNumber = new Map<number, AisleDefinition>();
  for (const aisle of aisles) {
    const existing = byNumber.get(aisle.number);
    if (!existing) {
      byNumber.set(aisle.number, aisle);
      continue;
    }
    const preferIncoming = existing.dataSource !== 'live' && aisle.dataSource === 'live';
    if (preferIncoming) {
      byNumber.set(aisle.number, aisle);
    }
  }
  return [...byNumber.values()].sort((a, b) => a.number - b.number);
}

export function mergeAisleDefinitions(
  liveAisles: AisleDefinition[],
  scaffoldAisles: AisleDefinition[],
): AisleDefinition[] {
  if (liveAisles.length === 0) {
    return dedupeAislesByNumber(scaffoldAisles);
  }

  const liveNumbers = new Set(liveAisles.map((aisle) => aisle.number));
  const supplemental = scaffoldAisles.filter(
    (aisle) =>
      aisle.dataSource !== 'live'
      && !liveNumbers.has(aisle.number)
      && !liveAisles.some((live) => live.name.toLowerCase() === aisle.name.toLowerCase()),
  );

  return dedupeAislesByNumber([...liveAisles, ...supplemental]);
}

/**
 * Canonical match: a walkthrough covers an aisle when its server-side
 * floorplanAisleIds contain the aisle's id. The aisle-number fallback keeps
 * legacy/demo walkthroughs linked, but never fabricates a canonical link.
 */
export function walkthroughCoversAisle(walk: AisleWalkthrough, aisle: AisleDefinition): boolean {
  if (walk.floorplanAisleIds && walk.floorplanAisleIds.length > 0) {
    return walk.floorplanAisleIds.includes(aisle.id);
  }
  return walk.aisleNumbers.includes(aisle.number);
}

export function getWalkthroughsForAisle(
  walkthroughs: AisleWalkthrough[],
  aisle: AisleDefinition,
): AisleWalkthrough[] {
  return walkthroughs.filter((walk) => walkthroughCoversAisle(walk, aisle));
}

export function getCampaignsForAisle(
  campaigns: CampaignPlacement[],
  aisle: AisleDefinition,
): CampaignPlacement[] {
  return campaigns.filter(
    (campaign) => campaign.aisleId === aisle.id || campaign.aisleNumber === aisle.number,
  );
}

function campaignNamesForAisle(campaigns: CampaignPlacement[], aisle: AisleDefinition): Set<string> {
  return new Set(
    getCampaignsForAisle(campaigns, aisle).map((campaign) => campaign.campaignName.toLowerCase()),
  );
}

export function getInstallImagesForAisle(
  profile: StoreProfileDetail,
  aisle: AisleDefinition,
): StoreImage[] {
  const campaignNames = campaignNamesForAisle(profile.campaigns, aisle);
  const seenUrls = new Set<string>();
  const results: StoreImage[] = [];

  for (const image of profile.images) {
    if (image.hidden) continue;
    if (seenUrls.has(image.url)) continue;

    const matchesCanonicalLink = image.floorplanAisleId === aisle.id;
    const matchesAisleNumber = image.aisleNumber === aisle.number;
    const captionLower = image.caption.toLowerCase();
    const matchesCampaign = Array.from(campaignNames).some((name) => captionLower.includes(name));

    if (matchesCanonicalLink || matchesAisleNumber || matchesCampaign) {
      seenUrls.add(image.url);
      results.push(image);
    }
  }

  for (const campaign of getCampaignsForAisle(profile.campaigns, aisle)) {
    for (const url of campaign.installationImageUrls) {
      if (!url || seenUrls.has(url)) continue;
      seenUrls.add(url);
      results.push({
        id: `campaign-img-${campaign.id}-${url}`,
        url,
        caption: `${campaign.campaignName} — ${campaign.formatLabel}`,
        captureDate: campaign.installationDate,
        surveyId: 'campaign-evidence',
        zone: 'media_location',
        aisleNumber: aisle.number,
        tags: ['install', campaign.brand, campaign.formatLabel],
        capturedBy: 'Field operator',
      });
    }
  }

  return results;
}

export function getReferenceImagesForAisle(profile: StoreProfileDetail, aisle: AisleDefinition) {
  return profile.referenceImages.filter((image) => image.floorplanAisleId === aisle.id);
}

export function getMediaLocationsForAisle(profile: StoreProfileDetail, aisle: AisleDefinition) {
  return profile.mediaLocations.filter(
    (media) => media.aisleId === aisle.id || media.floorplanAisleId === aisle.id,
  );
}

export function buildAisleMediaSummaries(profile: StoreProfileDetail): AisleMediaSummary[] {
  return profile.aisles.map((aisle) => {
    const walkthroughs = getWalkthroughsForAisle(profile.walkthroughs, aisle);
    const installImages = getInstallImagesForAisle(profile, aisle);
    const campaigns = getCampaignsForAisle(profile.campaigns, aisle);

    return {
      aisle,
      walkthroughs,
      installImages,
      campaignCount: campaigns.filter((c) => c.phase === 'current').length,
      hasWalkthrough: walkthroughs.length > 0,
      hasInstallImages: installImages.length > 0,
    };
  });
}

export function pickPrimaryWalkthroughForAisle(
  walkthroughs: AisleWalkthrough[],
  aisle: AisleDefinition,
): AisleWalkthrough | undefined {
  const matching = getWalkthroughsForAisle(walkthroughs, aisle);
  if (matching.length === 0) return undefined;
  return matching.sort((a, b) => b.captureDate.localeCompare(a.captureDate))[0];
}
